import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import type { Permission } from '@quds/shared';
import { Spinner } from '@/components/ui';
import { AccessDeniedPage } from '@/features/placeholder/AccessDeniedPage';
import { useAuth } from './AuthContext';

export const FORCED_PASSWORD_PATH = '/change-password';

export function RequireAuth() {
  const { status, user } = useAuth();
  const location = useLocation();
  if (status === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-canvas" role="status" aria-label="Checking your session">
        <Spinner />
      </div>
    );
  }
  if (status === 'anonymous') return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  if (user?.mustChangePassword && location.pathname !== FORCED_PASSWORD_PATH) {
    return <Navigate to={FORCED_PASSWORD_PATH} replace state={{ from: location.pathname + location.search }} />;
  }
  return <Outlet />;
}

/**
 * Renders children only when the signed-in user holds `permission`; otherwise
 * an Access Denied page. UI convenience only — the API enforces the same rule.
 */
export function RequirePermission({ permission, area, children }: { permission: Permission; area?: string; children: ReactNode }) {
  const { can } = useAuth();
  return can(permission) ? <>{children}</> : <AccessDeniedPage area={area} />;
}
